import axios from 'axios';
import { API_URL } from '../config/apiConfig';

// Create axios instance with default config
const api = axios.create({
  baseURL: `${API_URL}/api/resources`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const resourcesService = {
  // Get all documents
  getDocuments: async () => {
    try {
      const response = await api.get('/');
      return response.data;
    } catch (error) {
      console.error('Error fetching documents:', error);
      throw error;
    }
  },

  // Get categories
  getCategories: async () => {
    try {
      const response = await api.get('/categories');
      return response.data;
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }
  },

  // Download/view document
  downloadDocument: async (filename) => {
    try {
      const token = localStorage.getItem('token');
      const fileExtension = filename.toLowerCase().split('.').pop();
      
      // For PDFs, check if we're in a PWA environment
      if (fileExtension === 'pdf') {
        const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
                     window.navigator.standalone || 
                     document.referrer.includes('android-app://');
        
        if (isPWA) {
          // In PWA, return the PDF data for in-app viewing
          const response = await fetch(`${API_URL}/api/resources/download/${encodeURIComponent(filename)}`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const blob = await response.blob();
          const objectUrl = window.URL.createObjectURL(blob);
          
          return { 
            success: true, 
            isPWA: true, 
            pdfUrl: objectUrl, 
            filename: filename 
          };
        } else {
          // In regular browser, open in new tab
          const url = `${API_URL}/api/resources/download/${encodeURIComponent(filename)}`;
          const newWindow = window.open('', '_blank');
          
          // Use fetch to get the PDF blob with auth headers, then create object URL
          const response = await fetch(url, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });

          if (!response.ok) {
            newWindow.close();
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const blob = await response.blob();
          const objectUrl = window.URL.createObjectURL(blob);
          
          // Set the new window content to display the PDF
          newWindow.document.write(`
            <html>
              <head>
                <title>${filename}</title>
                <style>
                  body { margin: 0; padding: 0; }
                  iframe { width: 100vw; height: 100vh; border: none; }
                </style>
              </head>
              <body>
                <iframe src="${objectUrl}" type="application/pdf"></iframe>
              </body>
            </html>
          `);
          
          // Clean up the URL after the window is closed or after a delay
          setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60000); // 1 minute
        }
        
      } else {
        // For non-PDF files, download them
        const response = await fetch(`${API_URL}/api/resources/download/${encodeURIComponent(filename)}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        // Get filename from Content-Disposition header or use the original filename
        const contentDisposition = response.headers.get('Content-Disposition');
        let downloadFilename = filename;
        
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (filenameMatch && filenameMatch[1]) {
            downloadFilename = filenameMatch[1].replace(/['"]/g, '');
          }
        }

        // Download file
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = downloadFilename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }

      return { success: true };
    } catch (error) {
      console.error('Error downloading document:', error);
      throw error;
    }
  },

  // Refresh documents cache
  refreshDocuments: async () => {
    try {
      const response = await api.post('/refresh');
      return response.data;
    } catch (error) {
      console.error('Error refreshing documents:', error);
      throw error;
    }
  },
};

export default resourcesService;
